import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { canEdit, getUserType } from "../permissions";
import API_BASE from "../config";

const Registry = () => {
    const navigate = useNavigate();
    const userType = getUserType();
    const allowEdit = canEdit(userType, "registry");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        fetch(`${API_BASE}/api/allrows`, {
            method: "POST",
            headers: { Authorization: "1", "Content-Type": "application/json" },
            body: JSON.stringify({ token: "3232", table: "registry" }),
        })
            .then((res) => res.json())
            .then((json) => {
                setLoading(false);
                if (Array.isArray(json.data)) setData(json.data);
                else setError("Failed to load registry data.");
            })
            .catch((err) => {
                setLoading(false);
                setError("Error fetching registry.");
                console.error(err);
            });
    }, []);

    const filtered = data.filter((item) =>
        Object.values(item).join(" ").toLowerCase().includes(search.toLowerCase())
    );

    return (
        <>
            <style>{`
        .reg-page { display: flex; flex-direction: column; gap: 24px; }
        .reg-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
        .reg-title { font-size: 24px; font-weight: 700; color: #0f172a; }
        .reg-count { font-size: 14px; color: #64748b; margin-top: 4px; }
        .reg-controls { display: flex; gap: 12px; }
        .reg-search { padding: 10px 16px; border-radius: 10px; border: 1px solid #e2e8f0; width: 260px; outline: none; font-size: 14px; font-family: 'Inter', sans-serif; transition: all 0.2s; }
        .reg-search:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
        .reg-add-btn { padding: 10px 20px; background: #3b82f6; color: #fff; border: none; border-radius: 10px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px; font-family: 'Inter', sans-serif; transition: all 0.2s; }
        .reg-add-btn:hover { background: #2563eb; transform: translateY(-1px); }

        .reg-table-container { background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .reg-table { width: 100%; border-collapse: collapse; text-align: left; }
        .reg-table th { background: #f8fafc; padding: 16px 20px; font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; }
        .reg-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; color: #334155; font-size: 14px; }
        .reg-table tr:last-child td { border-bottom: none; }
        .reg-table tr:hover td { background: #f8fafc; }
        
        .reg-badge { padding: 4px 10px; border-radius: 99px; font-size: 12px; font-weight: 500; text-transform: capitalize; display: inline-block; }
        .reg-badge.scheduled { background: #eff6ff; color: #3b82f6; }
        .reg-badge.open { background: #fffbeb; color: #d97706; }
        .reg-badge.done { background: #ecfdf5; color: #059669; }

        .reg-action-btn { padding: 6px 12px; border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; color: #475569; font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .reg-action-btn:hover { background: #f1f5f9; color: #0f172a; border-color: #cbd5e1; }

        .reg-loading { padding: 60px; text-align: center; color: #64748b; font-size: 14px; }
      `}</style>

            <div className="reg-page">
                <div className="reg-header">
                    <div>
                        <h1 className="reg-title">Registry Management</h1>
                        <div className="reg-count">{loading ? "Loading..." : `${filtered.length} records found`}</div>
                    </div>
                    <div className="reg-controls">
                        <input
                            className="reg-search"
                            placeholder="Search registry..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {/* Additional action: Add Registry could be here */}
                    </div>
                </div>

                {error && <div style={{ padding: "12px", background: "#fef2f2", color: "#dc2626", borderRadius: "8px", border: "1px solid #fecaca" }}>{error}</div>}

                <div className="reg-table-container">
                    {loading ? (
                        <div className="reg-loading">Loading registry data...</div>
                    ) : filtered.length === 0 ? (
                        <div className="reg-loading">No registry records found.</div>
                    ) : (
                        <table className="reg-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Booking Id</th>
                                    <th>Customer Name</th>
                                    <th>Open Date</th>
                                    <th>Done Date</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.r_id || "-"}</td>
                                        <td>{item.booking_id || "-"}</td>
                                        <td style={{ fontWeight: 500, color: "#0f172a" }}>{item.cust_name || item.name || "Unknown"}</td>
                                        <td>{item.open_date || "-"}</td>
                                        <td>{item.done_date || "-"}</td>
                                        <td>
                                            <span className={`reg-badge ${(item.status || "open").toLowerCase()}`}>
                                                {item.status || "Open"}
                                            </span>
                                        </td>
                                        <td>
                                            {allowEdit ? (
                                                <button
                                                    className="reg-action-btn"
                                                    onClick={() => navigate("/dashboard/registry/edit", { state: { registry: item } })}
                                                >
                                                    ✏️ Edit
                                                </button>
                                            ) : (
                                                <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>View Only</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    );
};

export default Registry;
